import { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api.config';
import { getRedirectUri, getLogoutUri, LOCAL_CONFIG } from '../config/local.config';

/**
 * Hook per gestire l'autenticazione con AWS Cognito
 */
export const useCognito = () => {
  const [user, setUser] = useState(null);
  const [backendUser, setBackendUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Configurazione Cognito centralizzata per tre ambienti: development, labs, production
  const COGNITO_CONFIG = LOCAL_CONFIG.COGNITO;

  // Funzione per decodificare il JWT (solo per lettura, senza validazione)
  const decodeJWT = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  };

  // Funzione per scambiare authorization code con token
  const exchangeCodeForTokens = async (code) => {
    try {
      // Usa l'URL appropriato per l'ambiente (development, labs, production)
      const redirectUri = getRedirectUri();

      const tokenEndpoint = `https://${COGNITO_CONFIG.domain}/oauth2/token`;

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: COGNITO_CONFIG.client_id,
          code: code,
          redirect_uri: redirectUri
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Token exchange failed: ${response.status} - ${errorText}`);
      }

      const tokenData = await response.json();

      return {
        accessToken: tokenData.access_token,
        idToken: tokenData.id_token,
        refreshToken: tokenData.refresh_token
      };
    } catch (error) {
      return null;
    }
  };

  // Funzione per estrarre i token dal URL dopo il redirect di Cognito
  const extractTokensFromURL = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    // Controlla se ci sono errori nel callback
    if (error) {
      return getStoredTokens();
    }

    if (code) {
      const tokens = await exchangeCodeForTokens(code);

      if (tokens) {
        return tokens;
      }
    }

    // Se non c'è code o lo scambio è fallito, controlla i token salvati
    return getStoredTokens();
  };

  // Funzione per recuperare i token dal localStorage
  const getStoredTokens = () => {
    try {
      const accessToken = localStorage.getItem('cognito_access_token');
      const idToken = localStorage.getItem('cognito_id_token');
      return { accessToken, idToken };
    } catch (error) {
      return { accessToken: null, idToken: null };
    }
  };

  // Funzione per salvare i token nel localStorage
  const saveTokens = (accessToken, idToken) => {
    try {
      if (accessToken) localStorage.setItem('cognito_access_token', accessToken);
      if (idToken) localStorage.setItem('cognito_id_token', idToken);
    } catch (error) {
      // Silent error
    }
  };

  // Funzione per recuperare l'utente dal backend usando l'email
  const fetchUserFromBackend = async (email) => {
    try {
      // In development, always return user with id=1
      if (process.env.NODE_ENV === 'development') {
        const devUser = {
          id: 3,
          name: 'Developer',
          username: 'devuser1',
          email: 'dev@example.com',
          surname: 'User',
          role_id: 1,
          role: 'Admin',
          org_role_id: 2
        };
        return devUser;
      }

      console.log('🔍 Fetching user from backend API for email:', email);
      const response = await fetch(API_ENDPOINTS.USERS);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error ${response.status}: Failed to fetch users - ${errorText}`);
      }

      const data = await response.json();
      const users = data.users || data || [];

      // Trova l'utente con l'email corrispondente
      const foundUser = users.find(user =>
        user.email && user.email.toLowerCase() === email.toLowerCase()
      );

      if (foundUser) {
        console.log('🔍 Found user from backend:', foundUser);
        return foundUser;
      } else {
        console.log('🔍 No user found in backend for email:', email);
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  // Funzione per ottenere le informazioni dell'utente dal token ID
  const getUserFromToken = (idToken) => {
    if (!idToken) return null;

    const decoded = decodeJWT(idToken);
    if (!decoded) return null;

    return {
      email: decoded.email,
      name: decoded.name || decoded.given_name,
      surname: decoded.family_name,
      username: decoded['cognito:username'] || decoded.preferred_username,
      sub: decoded.sub,
      groups: decoded['cognito:groups'] || [],
      // Estrai altri campi come necessario
    };
  };

  // Funzione per verificare se il token è ancora valido
  const isTokenValid = (token) => {
    if (!token) return false;

    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp > currentTime;
  };

  // Funzione di logout
  const logout = () => {
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_id_token');
    setUser(null);
    setBackendUser(null);

    // Redirect a Cognito logout URL
    const logoutUri = getLogoutUri();
    const encodedLogoutUri = encodeURIComponent(logoutUri);
    const cognitoLogoutUrl = `https://${COGNITO_CONFIG.domain}/logout?client_id=${COGNITO_CONFIG.client_id}&logout_uri=${encodedLogoutUri}`;

    window.location.href = cognitoLogoutUrl;
  };

  // Funzione per login (redirect a Cognito)
  const login = () => {
    const redirectUri = getRedirectUri();
    const encodedRedirectUri = encodeURIComponent(redirectUri);
    const loginUrl = `https://${COGNITO_CONFIG.domain}/login?client_id=${COGNITO_CONFIG.client_id}&response_type=code&scope=email+openid+phone&redirect_uri=${encodedRedirectUri}`;

    // Debug: log della configurazione utilizzata
    console.log('🔍 Debug Login Configuration:');
    console.log('NODE_ENV:', process.env.NODE_ENV);
    console.log('COGNITO_CONFIG:', COGNITO_CONFIG);
    console.log('Login URL:', loginUrl);

    window.location.href = loginUrl;
  };

  // Effect per inizializzare l'autenticazione
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);

      try {
        // Mock user for local development
        if (process.env.NODE_ENV === 'development') {
          const mockUser = {
            email: 'dev@example.com',
            name: 'Developer',
            surname: 'User',
            username: 'devuser',
            sub: 'mock-sub-123',
            groups: ['developers']
          };

          // In development, always use backend user with id=3 and org_role_id=2 (People Manager)
          const mockBackendUser = {
            id: 3,
            name: 'Developer',
            username: 'devuser',
            email: 'dev@example.com',
            surname: 'User',
            role_id: 1,
            role: 'Admin',
            org_role_id: 2  // People Manager per vedere la People Manager Area
          };

          console.log('🔍 Setting mock backend user:', mockBackendUser);
          setUser(mockUser);
          setBackendUser(mockBackendUser);
          setLoading(false);
          return;
        }

        // Estrai token dall'URL o dal localStorage
        const tokens = await extractTokensFromURL();
        const { accessToken, idToken } = tokens || {};

        if (idToken && isTokenValid(idToken)) {
          const userInfo = getUserFromToken(idToken);

          if (userInfo) {
            setUser(userInfo);
            saveTokens(accessToken, idToken);

            // Recupera l'utente dal backend usando l'email di Cognito
            const backendUserData = await fetchUserFromBackend(userInfo.email);
            if (backendUserData) {
              setBackendUser(backendUserData);
            } else {
              setBackendUser(null);
            }

            // Pulisci l'URL dai parametri di auth se presenti
            if (window.location.search.includes('code=')) {
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          } else {
            setUser(null);
            setBackendUser(null);
          }
        } else {
          // Token non valido o non presente
          setUser(null);
          setBackendUser(null);
        }
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  return {
    user,
    backendUser,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    saveTokens,
    fetchUserFromBackend
  };
}; 